import { formatMoney, type Centavos, type Milli } from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { descuentoDe, nombreDeLleva, type ResultadoDeOferta } from '../lib/ofertas.js';
import { cantidadCorta } from '../i18n/dominio.js';
import { useT, type Clave } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { fechaCorta } from './CuentasDeCaja.js';
import { Miniatura } from './Miniatura.js';

/** A pace a week, to a tenth: «14», «1.8 lb». */
export const porSemana = (m: Milli, unidadBase: string) => cantidadCorta((Math.round(m / 100) * 100) as Milli, unidadBase);

/** How many times the pace before: «×15», «×1.4». */
const veces = (n: number) => `×${n >= 10 ? Math.round(n) : n.toFixed(1)}`;

/** A figure under its name, in a cell of the card's grid. */
function Cifra({ etiqueta, children, dato }: { etiqueta: string; children: React.ReactNode; dato: string }) {
  return (
    <div className="min-w-0" data-cifra={dato}>
      <dt className="text-xs text-slate-500">{etiqueta}</dt>
      <dd className="tabular text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * What an offer did, on «Ofertas» › «Resultados» (`resultadosDeOfertas`): its product, its days, why
 * it was put on and how much it took off; whether it runs; what sold at it; its product's pace a week
 * on its list while it ran against the four weeks before it, and how many times that is; what it
 * brought in, what the customers did not pay, and, for whoever sees costs, what it left over cost;
 * what is left of the product now; for an expiring batch, what is left of it and what was thrown out
 * as expired; and, a week after it ended, the pace since, which says whether the customers only
 * bought ahead. On its first day it has no pace yet: a day's sales are not a week's.
 */
export function TarjetaResultado({
  resultado: r,
  producto,
  regular,
  verCostos,
}: {
  resultado: ResultadoDeOferta;
  producto: Producto | undefined;
  /** The list's price now of the first presentation it named, for how much it took off (none at so many for fewer). */
  regular: Centavos | null;
  verCostos: boolean;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const o = r.oferta;
  const base = producto?.unidadBase ?? 'unidad';
  const primera = o.precios[0];
  // How much it took off, «−30%», or so many for fewer, «2×1».
  const quita = !primera
    ? null
    : 'lleva' in primera
      ? nombreDeLleva({ cantidad: primera.lleva, paga: primera.paga })
      : regular !== null && regular > primera.precio
        ? `−${Math.round(descuentoDe(primera.precio, regular) / 100)}%`
        : null;
  // A day is not a week's pace: on its first day, none yet, as its card under «En oferta» says.
  const unDia = r.dias === 1;
  const ritmo = unDia
    ? '—'
    : r.porSemanaAntes > 0
      ? t('ofertas.r.ritmoValor', { durante: porSemana(r.porSemanaDurante, base), antes: porSemana(r.porSemanaAntes, base) })
      : t('ofertas.r.ritmoNuevo', { durante: porSemana(r.porSemanaDurante, base) });

  return (
    <li className="tarjeta p-3" data-resultado={o.id} data-resultado-producto={o.productoId} data-resultado-estado={r.estado}>
      <div className="flex items-start gap-3">
        {producto && <Miniatura producto={producto} tamano="lg" />}
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{producto ? nombre(producto) : t('pos.producto')}</p>
          <p className="tabular mt-0.5 text-xs text-slate-400" data-resultado-dias>
            {`${fechaCorta(o.desde)} – ${fechaCorta(r.hasta)}`}
            {' · '}
            {t(`ofertas.motivo.${o.motivo}` as Clave)}
            {quita !== null && ` · ${quita}`}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-md px-2 py-0.5 text-xs ring-1 ring-inset ${
            r.estado === 'corre' ? 'bg-teal-500/12 text-teal-300 ring-teal-500/20' : 'bg-slate-700/50 text-slate-300 ring-white/5'
          }`}
        >
          {t(r.estado === 'corre' ? 'ofertas.corre' : 'ofertas.termino')}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
        <Cifra etiqueta={t('ofertas.r.vendidas')} dato="vendidas">
          {cantidadCorta(r.vendidas, base)}
        </Cifra>
        <Cifra etiqueta={t('ofertas.r.ritmo')} dato="ritmo">
          {ritmo}
          {r.veces !== null && !unDia && <span className="ml-1 text-teal-300">{veces(r.veces)}</span>}
        </Cifra>
        <Cifra etiqueta={t('ofertas.r.ingreso')} dato="ingreso">
          {formatMoney(r.ingreso)}
        </Cifra>
        <Cifra etiqueta={t('ofertas.r.descuento')} dato="descuento">
          {formatMoney(r.descuento)}
        </Cifra>
        {verCostos && (
          <Cifra etiqueta={t('ofertas.r.ganancia')} dato="ganancia">
            <span className={r.ganancia < 0 ? 'text-rose-300' : ''}>{formatMoney(r.ganancia)}</span>
          </Cifra>
        )}
        <Cifra etiqueta={t('ofertas.r.quedan')} dato="quedan">
          {cantidadCorta(r.quedan, base)}
        </Cifra>
      </dl>

      {r.lote && (
        <p className="mt-2 text-xs text-slate-400" data-resultado-lote>
          {t(r.estado === 'corre' ? 'ofertas.r.loteCorre' : 'ofertas.r.lote', { fecha: fechaCorta(o.hasta), quedan: cantidadCorta(r.lote.quedan, base) })}
          {r.lote.botadas > 0 && (
            <span className="text-amber-300">
              {' · '}
              {t('ofertas.r.botadas', { n: cantidadCorta(r.lote.botadas, base) })}
            </span>
          )}
        </p>
      )}
      {r.porSemanaDespues !== null && (
        <p className="mt-1 text-xs text-slate-400" data-resultado-despues>
          {t('ofertas.r.despues', { n: porSemana(r.porSemanaDespues, base) })}
        </p>
      )}
    </li>
  );
}
